import { useEffect, useState, useCallback } from 'react'
import { X, Search, Trash2, Loader2, UserMinus } from 'lucide-react'
import {
  useGetPersonQuery,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useUnlinkPersonMutation,
} from '@/store/api'
import { faceCropUrl, signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface PersonProfileModalProps {
  personId: string
  onClose: () => void
  onFindImages: (personId: string) => void
  onViewFace: (faceId: string) => void
}

/** Build crop URL from face_id, matching the backend pattern. */
function cropUrlFromFaceId(faceId: string): string {
  return faceCropUrl(faceId)
}

export default function PersonProfileModal({
  personId,
  onClose,
  onFindImages,
  onViewFace,
}: PersonProfileModalProps) {
  useSignedUrls()
  const { data: person, isLoading, isError } = useGetPersonQuery(personId)
  const [updatePerson] = useUpdatePersonMutation()
  const [deletePerson] = useDeletePersonMutation()
  const [unlinkPerson] = useUnlinkPersonMutation()

  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [isSavingName, setIsSavingName] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [unlinkingFaceId, setUnlinkingFaceId] = useState<string | null>(null)

  // Initialize edit name from person data
  useEffect(() => {
    if (person?.name) {
      setEditName(person.name)
    }
  }, [person?.name])

  // Keyboard: Escape closes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmDelete) {
          setConfirmDelete(false)
        } else if (isEditing) {
          setIsEditing(false)
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, confirmDelete, isEditing])

  const handleSaveName = useCallback(async () => {
    if (!person || isSavingName) return
    const trimmed = editName.trim()
    if (!trimmed) return

    setIsSavingName(true)
    try {
      await updatePerson({ personId, name: trimmed }).unwrap()
      setIsEditing(false)
    } catch (error: unknown) {
      console.error('Failed to rename person:', error)
    } finally {
      setIsSavingName(false)
    }
  }, [person, editName, personId, isSavingName, updatePerson])

  const handleUnlinkFace = useCallback(async (faceId: string) => {
    if (unlinkingFaceId) return
    setUnlinkingFaceId(faceId)
    try {
      await unlinkPerson({ faceIds: [faceId] }).unwrap()
    } catch (error: unknown) {
      console.error('Failed to unlink face:', error)
    } finally {
      setUnlinkingFaceId(null)
    }
  }, [unlinkPerson, unlinkingFaceId])

  const handleDeletePerson = useCallback(async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      await deletePerson(personId).unwrap()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to delete person:', error)
    } finally {
      setIsDeleting(false)
    }
  }, [personId, isDeleting, deletePerson, onClose])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
          <h2 className="text-sm font-semibold text-foreground">Person Profile</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-secondary transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-48">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">Failed to load person details.</p>
            </div>
          )}

          {person && !isLoading && (
            <>
              {/* Person info header */}
              <div className="flex gap-4 items-start">
                {/* Prototype face */}
                <div className="w-24 h-24 rounded-full overflow-hidden bg-secondary flex-shrink-0 border-2 border-border">
                  {person.prototype_crop_url ? (
                    <img
                      src={signedSrc(person.prototype_crop_url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-2xl">
                      ?
                    </div>
                  )}
                </div>

                {/* Name + stats */}
                <div className="flex-1 min-w-0 pt-1">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName()
                          if (e.key === 'Escape') setIsEditing(false)
                        }}
                        className="w-full px-2 py-1 rounded bg-secondary border border-cyan-500 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-cyan-500"
                        placeholder="Enter name..."
                        autoFocus
                      />
                      <button
                        onClick={handleSaveName}
                        disabled={isSavingName}
                        className="px-2 py-1 text-xs font-medium rounded bg-cyan-600 hover:bg-cyan-700 text-white transition-colors disabled:opacity-50 flex-shrink-0"
                      >
                        {isSavingName ? '...' : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditName(person.name || '')
                        setIsEditing(true)
                      }}
                      className="text-left w-full"
                    >
                      <span className={`text-lg font-semibold ${person.name ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                        {person.name || 'Unnamed person'}
                      </span>
                    </button>
                  )}

                  <div className="text-xs text-muted-foreground mt-1.5">
                    {person.face_count} {person.face_count === 1 ? 'face' : 'faces'} in {person.image_count} {person.image_count === 1 ? 'image' : 'images'}
                  </div>
                </div>
              </div>

              {/* All Faces grid */}
              <div className="mt-5 pt-4 border-t border-border">
                <div className="text-xs font-medium text-muted-foreground mb-3">
                  All Faces ({person.face_ids.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {person.face_ids.map((faceId) => {
                    const cropUrl = cropUrlFromFaceId(faceId)
                    return (
                      <div key={faceId} className="relative group">
                        <button
                          onClick={() => onViewFace(faceId)}
                          className="w-14 h-14 rounded-lg overflow-hidden bg-secondary border border-border hover:border-cyan-500 transition-colors"
                        >
                          {cropUrl && (
                            <img
                              src={cropUrl}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          )}
                        </button>
                        {/* Unlink X button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUnlinkFace(faceId)
                          }}
                          disabled={unlinkingFaceId === faceId}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-card border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 hover:border-red-600 hover:text-white text-muted-foreground"
                          title="Remove from person"
                        >
                          {unlinkingFaceId === faceId ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <UserMinus size={10} />
                          )}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-5 pt-4 border-t border-border flex items-center gap-2">
                <button
                  onClick={() => onFindImages(personId)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Search size={14} />
                  Find Images
                </button>

                {/* Delete with confirmation */}
                {confirmDelete ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs text-muted-foreground">Delete person?</span>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="px-2 py-1 text-xs rounded bg-secondary hover:bg-secondary/80 text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeletePerson}
                      disabled={isDeleting}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Delete
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/10 hover:bg-red-600/20 text-red-400 rounded-lg text-sm transition-colors ml-auto"
                  >
                    <Trash2 size={14} />
                    Delete Person
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
